import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, MapPin, ChevronDown, Youtube, Phone, ShoppingBag } from "lucide-react";
import Logo from "@/components/Logo";
import { useCentre } from "@/context/CentreContext";
import { useUI } from "@/context/UIContext";
import { useCart } from "@/context/CartContext";

const FILE_SERVICE_URL = "https://horsepowertuningfiles.com/";
const YOUTUBE_URL = "https://www.youtube.com/@HorsePowerGarage/videos";
const ACADEMY_URL = "https://horsepower-academy.com/";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/reprogrammation", label: "Reprogrammation" },
  { to: "/services", label: "Services" },
  { to: "/centres", label: "Centres" },
  { to: "/realisations", label: "Réalisations" },
  { to: "/nous-connaitre", label: "Nous connaître" },
  { to: "/boutique", label: "Boutique" },
];

export default function Header() {
  const { current, setSelectorOpen } = useCentre();
  const { openLead } = useUI();
  const { count } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="fixed top-0 inset-x-0 z-[90]" data-testid="site-header">
      {/* Slim utility bar */}
      <div className="hidden lg:block bg-hp-dark/95 backdrop-blur border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-11 flex items-center justify-between text-[13px] tracking-wide">
          <div className="flex items-center gap-6 text-white/85">
            <button onClick={() => setSelectorOpen(true)} className="flex items-center gap-1.5 hover:text-hp-yellow transition-colors font-medium" data-testid="header-centre-selector">
              <MapPin size={15} className="text-hp-yellow" /> {current ? current.city : "Choisir mon centre"} <ChevronDown size={13} />
            </button>
            {current?.phone && <a href={`tel:${current.phone.replace(/\s/g,"")}`} className="flex items-center gap-1.5 hover:text-hp-yellow transition-colors font-medium"><Phone size={14} className="text-hp-yellow" /> {current.phone}</a>}
          </div>
          <div className="flex items-center gap-7">
            <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-white/90 hover:text-hp-yellow transition-colors uppercase font-semibold" data-testid="header-youtube"><Youtube size={16} className="text-red-500" /> Horsepower TV</a>
            <a href={ACADEMY_URL} target="_blank" rel="noopener noreferrer" className="text-white/90 hover:text-hp-yellow transition-colors uppercase font-semibold" data-testid="header-academy">Formation</a>
            <a href={FILE_SERVICE_URL} target="_blank" rel="noopener noreferrer" className="text-white/90 hover:text-hp-yellow transition-colors uppercase font-semibold" data-testid="header-file-service">File Service</a>
            <Link to="/devenir-franchise" className="bg-hp-yellow text-hp-dark hover:bg-hp-gold transition-colors uppercase font-bold px-4 py-1.5 rounded-sm" data-testid="header-franchise">Devenir franchisé</Link>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className={`transition-colors duration-300 ${scrolled ? "bg-hp-dark/90 backdrop-blur-xl border-b border-white/8" : "bg-gradient-to-b from-black/70 to-transparent lg:bg-hp-dark/70 lg:backdrop-blur"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 lg:h-[68px] flex items-center justify-between gap-4">
          <Logo />
          <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"}
                className={({ isActive }) => `text-[12px] xl:text-[13px] whitespace-nowrap uppercase tracking-wide transition-colors font-medium ${isActive ? "text-hp-yellow" : "text-white/85 hover:text-hp-gold"}`}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s|è|î/g, "-")}`}>{n.label}</NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2.5">
            <Link to="/boutique/panier" className="relative text-white hover:text-hp-yellow p-1.5 transition-colors" data-testid="header-cart" aria-label="Panier">
              <ShoppingBag size={22} />
              {count > 0 && <span className="absolute -top-0.5 -right-0.5 bg-hp-yellow text-black text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center" data-testid="header-cart-count">{count}</span>}
            </Link>
            <button onClick={() => openLead({ request_type: "devis" })} className="hidden sm:inline-flex hp-cta text-xs px-5 py-2.5 whitespace-nowrap" data-testid="header-devis">Demander un devis</button>
            <button className="lg:hidden text-white p-1" onClick={() => setMobileOpen((o) => !o)} data-testid="mobile-menu-toggle">
              {mobileOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-hp-dark border-t border-white/10 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <nav className="flex flex-col px-4 py-3">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"}
                className={({ isActive }) => `py-3 border-b border-white/5 text-sm uppercase tracking-wide font-medium ${isActive ? "text-hp-yellow" : "text-white/85"}`}>{n.label}</NavLink>
            ))}
            <a href={FILE_SERVICE_URL} target="_blank" rel="noopener noreferrer" className="py-3 border-b border-white/5 text-sm uppercase tracking-wide text-white/85">File Service</a>
            <a href={ACADEMY_URL} target="_blank" rel="noopener noreferrer" className="py-3 border-b border-white/5 text-sm uppercase tracking-wide text-white/85">Formation reprogrammation</a>
            <Link to="/devenir-franchise" className="py-3 border-b border-white/5 text-sm uppercase tracking-wide text-hp-gold font-bold">Devenir franchisé</Link>
            <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" className="py-3 border-b border-white/5 text-sm uppercase tracking-wide text-white/85 flex items-center gap-2"><Youtube size={16} /> Horsepower TV</a>
            <button onClick={() => setSelectorOpen(true)} className="py-3 text-left border-b border-white/5 text-sm uppercase tracking-wide text-white/85 flex items-center gap-2"><MapPin size={16} className="text-hp-gold" /> Centre : {current ? current.city : "Choisir"}</button>
            <button onClick={() => openLead({ request_type: "devis" })} className="hp-cta mt-4 py-3.5">Demander un devis</button>
          </nav>
        </div>
      )}
    </header>
  );
}
